import { FicheDessinee, type FicheEditHandlers } from '@/components/templates/FicheDessinee';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { Fiche } from '@pharmafiche/schema';
import { type ReactNode, createContext, useContext, useMemo } from 'react';

/** Inline CSS Transform helper — keeps us off the @dnd-kit/utilities sub-package. */
function transformToCss(
  t: { x: number; y: number; scaleX: number; scaleY: number } | null,
): string | undefined {
  if (!t) return undefined;
  return `translate3d(${t.x}px, ${t.y}px, 0) scaleX(${t.scaleX}) scaleY(${t.scaleY})`;
}

interface SortableFicheLayerProps {
  fiche: Fiche;
  editHandlers: FicheEditHandlers;
}

/**
 * Wraps FicheDessinee with a DndContext in edit mode. Section reordering uses
 * a vertical-list strategy (sections drag up/down within the same flat list).
 * Each `[data-section-id]` element in FicheDessinee becomes a drag target.
 * The drag handle is the small ⠿ button rendered by CardActions.
 */
export function SortableFicheLayer({ fiche, editHandlers }: SortableFicheLayerProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const sectionIds = useMemo(() => fiche.sections.map((s) => s.id), [fiche.sections]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = sectionIds.indexOf(String(active.id));
    const to = sectionIds.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    editHandlers.reorderSections?.(from, to);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
        <FicheDessinee fiche={fiche} editHandlers={editHandlers} />
      </SortableContext>
    </DndContext>
  );
}

interface SortableSectionProps {
  sectionId: string;
  children: ReactNode;
}

/**
 * Wrapper around a single section card. Connects it to @dnd-kit and exposes
 * the drag handle props to descendants via SortableHandleContext.
 */
export function SortableSection({ sectionId, children }: SortableSectionProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sectionId,
  });

  const style: React.CSSProperties = {
    transform: transformToCss(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} data-section-id={sectionId}>
      <SortableHandleContext.Provider value={{ listeners, attributes }}>
        {children}
      </SortableHandleContext.Provider>
    </div>
  );
}

interface SortableHandleApi {
  listeners: ReturnType<typeof useSortable>['listeners'];
  attributes: ReturnType<typeof useSortable>['attributes'];
}

const SortableHandleContext = createContext<SortableHandleApi | null>(null);

/** Hook for the drag handle button to grab the sortable props. */
export function useSortableHandle(): SortableHandleApi | null {
  return useContext(SortableHandleContext);
}
